import throttler from './throttler';
import { DESTINATION } from './constants';
import stateManager from './stateManager';
import { init as adobeUtilsInit } from './adobeUtils';
import { noWindow } from './noWindow';
import { resetSendGate, allowSends } from './sendGate';
import { get as getEnvironmentContext } from './getEnvironmentContext';
import { init as errorEmitterInit, ErrorHandler } from './errorEmitter';
import logger from './logger';
import { GenericObject } from './helperTypes';

// Client init arguments:
export type AuthState = 'hard_logged_in' | 'soft_logged_in' | 'logged_out';
export type Property = 'easybib' | 'citationmachine' | 'citethisforme' | 'bibme' | 'chegg';
export type Product = 'easybib' | 'math' | 'tutors' | 'test_prep' | 'text_books' | 'bibme' | 'citation_machine' | 'internships' | 'chegg_writing'| 'easybib_plus'| 'citation_machine_plus'| 'cite_this_for_me_plus'| 'bibme_plus' | 'capp' | 'chegg_study';
export type BusinessUnit = 'wt' | 'bm' | 'cs' | 'core' | 'cm' | 'eb' | 'fc' | 'help' | 'math' | 'tb' | 'tp' | 'tut' | 'outcomes' | 'partner' | 'nanorep' | 'prep';
export type AppId = 'cfe' | 'wfe' | 'capp';
export type SubscriptionStatus = 'active' | 'cancelled' | 'non_member' | 'paused';
export type TestCell = {
    layer: string;
    cell: string;
};
export type TestCellArray = TestCell[]
export type TestCellObj = {
    [x: string]: string;
};

export type EndpointOptions = {
    withCredentials?: boolean;
    headers?: GenericObject;
}

export interface SessionData {
    pageType: string;
    appId: AppId;
    property: Property;
    subscriptionStatus: SubscriptionStatus;
    authState: AuthState;
    product: Product;
    businessUnit: BusinessUnit;
    endpoint?: string;
    endpointOptions?: EndpointOptions;
    testCells?: TestCellArray | TestCellObj;
    appVersion?: string;
    cheggUuid?: string;
    clientIpAddress?: string;
    staging?: boolean;
    errorHandler?: ErrorHandler;
    userAgent?: string;
    referrer?: string;
    url?: string;
};

export type InitClientFn = (sessionData: SessionData) => void;

export type initializer = {
    initClient: InitClientFn
    reset: () => void;
    initFailureCountdown: (countdown: number) => void;
};

const initializer = {} as initializer;

let isInit = false;
// Hoist the resolve function, so it can be called externally when init is done:
const completeInit = () => {
    // Resolve the initPromise, signalling to AF that events can now be sent:
    allowSends();
    isInit = true;
};

// Mainly for unit testing:
initializer.reset = () => {
    stateManager.reset();
    resetSendGate();
    isInit = false;
};

initializer.initClient = (sessionData) => {
    if (isInit) {
        // Double init shouldn't happen, but if it does, send an error and clear
        // the state to avoid mixed-state events
        sendDoubleInitError(sessionData);
        initializer.reset();
    }

    // Extract only what's needed, and default as needed:
    const {
        pageType,
        appId,
        property,
        subscriptionStatus,
        authState,
        product,
        businessUnit,
        appVersion,
        cheggUuid,
        errorHandler,
        clientIpAddress,
        referrer,
        url,
        staging = false,
        testCells = [],
        endpoint: endpointInput,
        endpointOptions: endpointOptions,
        userAgent: userAgentInput,
    } = sessionData;

    // Set error handling behavior:
    errorEmitterInit(errorHandler);

    // Begin the adobe user data acquisition. It won't be needed till the first
    // event is sent, so the `await` can wait till then.
    adobeUtilsInit().catch(logger.error);

    // Set endpoint based on config or environment:
    const endpoint = getEndpoint(staging, endpointInput);

    // Get rio-sdk-web version to send along with requests:
    const afVersion = process.env.VERSION;

    // Derive user agent from session data or window scope.
    const userAgent = getUserAgent(userAgentInput);

    // Get browser data to include with requests:
    const {
        browserName,
        browserVersion,
        osName,
        osVersion,
        platform,
     } = getEnvironmentContext(userAgent);

    const initialState = {
        userAgent,
        url,
        referrer,
        afVersion,
        browserName,
        browserVersion,
        endpoint,
        endpointOptions,
        pageType,
        appId,
        property,
        subscriptionStatus,
        authState,
        appVersion,
        cheggUuid,
        clientIpAddress,
        staging,
        testCells,
        product,
        osName,
        osVersion,
        platform,
        businessUnit,
    };

    // Pass session data to state:
    stateManager.update(initialState);

    // Signal to the app that init has taken place:
    completeInit();

    // Relay all events that accumulated before client was init:
    throttler.startCountdown();
};

const getEndpoint = (staging: boolean, endpointInput?: string): string => {
    let endpoint;
    if (endpointInput) {
        endpoint = endpointInput;
    } else if (staging) {
        endpoint = DESTINATION.staging;
    } else {
        endpoint = DESTINATION.production;
    };
    return endpoint;
};

const getUserAgent = (userAgentInput?: string): string => {
    let userAgent;
    if (userAgentInput) {
        userAgent = userAgentInput;
    } else if (!noWindow()) {
        userAgent = window.navigator.userAgent;
    } else {
        userAgent = 'unknown';
    }
    return userAgent;
};

// Send error upon duplicate init:
const sendDoubleInitError = (sessionData: SessionData): void => {
    const stateComparison = JSON.stringify({
        originalState: stateManager.get(),
        newState: sessionData,
    });
    const msg = `init is called twice: ${stateComparison}`;
    logger.error(new Error(msg));
}

// Send error if init takes too long / doesn't happen:
initializer.initFailureCountdown = (initTimeoutThreshold) => {
    // Account for server-side render:
    // tslint:disable-next-line: strict-type-predicates
    if (noWindow()) {
        return
    }
    setTimeout(() => {
        if (!isInit) {
            const slowInitError = new Error(`init was not called in ${initTimeoutThreshold} milliseconds`);
            logger.error(slowInitError);
        }
    }, initTimeoutThreshold);
}

export default initializer;
